/**
 * Validates if a parsed value is a valid positive integer without decimals
 * @param {number} parsedInt - Value from parseInt
 * @param {number} parsedFloat - Value from parseFloat
 * @returns {boolean} True if valid
 */
const isValidPositiveInteger = (parsedInt, parsedFloat) => {
  const isValidInteger = Number.isInteger(parsedInt);
  const isPositive = parsedInt > 0;
  const hasNoDecimals = parsedInt === parsedFloat;

  return isValidInteger && isPositive && hasNoDecimals;
};

/**
 * Validate and parse page number from string
 * @param {string | null} pageParam - Page parameter from URL
 * @param {number} defaultPageNumber - Default page number
 * @returns {number} Valid page number
 */
export const parsePageNumber = (pageParam, defaultPageNumber) => {
  if (!pageParam) return defaultPageNumber;

  const parsedPage = parseInt(pageParam, 10);
  const parsedFloat = parseFloat(pageParam);

  if (isValidPositiveInteger(parsedPage, parsedFloat)) {
    return parsedPage;
  }

  return defaultPageNumber;
};

/**
 * Validate page number against constraints
 * @param {number} page - Page number to validate
 * @param {number} totalPages - Total number of pages
 * @returns {number} Valid page number
 */
export const validatePageNumber = (page, totalPages) => Math.max(1, Math.min(page, totalPages));

/**
 * Update URL search parameters
 * @param {URLSearchParams} prevParams - Previous search parameters
 * @param {Record<string, string | number | null | undefined>} updates - Updates to apply
 * @returns {URLSearchParams} Updated search parameters
 */
export const updateSearchParams = (prevParams, updates) => {
  const newParams = new URLSearchParams(prevParams);

  Object.entries(updates).forEach(([key, value]) => {
    if (value !== null && value !== undefined) {
      newParams.set(key, value.toString());
    } else {
      newParams.delete(key);
    }
  });

  return newParams;
};

/**
 * Calculate optimal page number when page size changes
 * Maintains user's approximate position in the data set
 * @param {number} currentPage - Current page number
 * @param {number} currentPageSize - Current page size
 * @param {number} newPageSize - New page size
 * @returns {number} Optimal new page number
 */
export const calculateOptimalPageForSizeChange = (currentPage, currentPageSize, newPageSize) => {
  // Guard against non-numeric inputs (e.g. switching from/to "Tous" -> Number('none') === NaN)
  // and against non-positive sizes that would produce Infinity / negatives.
  if (!Number.isFinite(currentPage) || currentPage < 1) return 1;
  if (!Number.isFinite(currentPageSize) || currentPageSize <= 0) return 1;
  if (!Number.isFinite(newPageSize) || newPageSize <= 0) return 1;

  const currentFirstItemIndex = (currentPage - 1) * currentPageSize;

  const optimalNewPage = Math.floor(currentFirstItemIndex / newPageSize) + 1;

  return Math.max(1, optimalNewPage);
};

/**
 * Marker tokens used in the page list returned by `calculatePaginationRange` to
 * indicate where ellipses should be displayed by the UI.
 */
export const PAGINATION_ELLIPSIS_LEFT = 'ellipsis-left';
export const PAGINATION_ELLIPSIS_RIGHT = 'ellipsis-right';

/**
 * @typedef {number | typeof PAGINATION_ELLIPSIS_LEFT | typeof PAGINATION_ELLIPSIS_RIGHT} PaginationRangeItem
 */

const range = (/** @type {number} */ start, /** @type {number} */ end) => {
  if (end < start) return [];
  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
};

/**
 * Build the visible page list for a classic numeric pagination control,
 * inserting ellipses on either side when there are too many pages to display.
 * Examples (siblingCount=1, boundaryCount=1):
 *   - (1, 5)   -> [1, 2, 3, 4, 5]
 *   - (1, 10)  -> [1, 2, 3, 4, 5, 'ellipsis-right', 10]
 *   - (22, 596) -> [1, 'ellipsis-left', 21, 22, 23, 'ellipsis-right', 596]
 *   - (595, 596) -> [1, 'ellipsis-left', 593, 594, 595, 596]
 * @param {number} currentPage - 1-based current page
 * @param {number} totalPages - total number of pages (>= 0)
 * @param {number} [siblingCount=1] - number of pages to show on each side of currentPage
 * @param {number} [boundaryCount=1] - number of pages always shown at the start/end
 * @returns {Array<PaginationRangeItem>} The page items (numbers and ellipsis markers)
 */
export const calculatePaginationRange = (
  currentPage,
  totalPages,
  siblingCount = 1,
  boundaryCount = 1,
) => {
  if (!Number.isFinite(totalPages) || totalPages <= 0) return [];

  const safeBoundary = Math.max(0, Math.floor(boundaryCount));
  const safeSibling = Math.max(0, Math.floor(siblingCount));
  const safeCurrent = Math.min(Math.max(1, Math.floor(currentPage) || 1), totalPages);

  // If everything fits, return the full range without ellipses.
  // Total slots when no ellipsis: 2*boundary + 2*sibling + 1 (current) + 2 (the two ellipsis slots we'd otherwise need)
  const totalNumbers = 2 * safeBoundary + 2 * safeSibling + 1 + 2;
  if (totalPages <= totalNumbers) return range(1, totalPages);

  const startPages = range(1, Math.min(safeBoundary, totalPages));
  const endPages = range(Math.max(totalPages - safeBoundary + 1, safeBoundary + 1), totalPages);

  const siblingsStart = Math.max(
    Math.min(safeCurrent - safeSibling, totalPages - safeBoundary - 2 * safeSibling - 1),
    safeBoundary + 2,
  );
  const siblingsEnd = Math.min(
    Math.max(safeCurrent + safeSibling, safeBoundary + 2 * safeSibling + 2),
    endPages.length > 0 ? endPages[0] - 2 : totalPages - 1,
  );

  const items = /** @type {Array<PaginationRangeItem>} */ ([
    ...startPages,
    ...(siblingsStart > safeBoundary + 2
      ? [PAGINATION_ELLIPSIS_LEFT]
      : range(safeBoundary + 1, Math.min(safeBoundary + 1, siblingsStart - 1))),
    ...range(siblingsStart, siblingsEnd),
    ...(siblingsEnd < totalPages - safeBoundary - 1
      ? [PAGINATION_ELLIPSIS_RIGHT]
      : range(Math.max(totalPages - safeBoundary, siblingsEnd + 1), totalPages - safeBoundary)),
    ...endPages,
  ]);

  return items;
};
